import { useState, type FormEvent } from 'react'
import { Alert, AlertDescription, Button, Input } from '@/components/ui'
import { DETAIL_TITLE_CLASS } from '@/components/layout/PageHeader'
import { Plus, Trash2 } from 'lucide-react'
import type { Persona } from '@/stores'
import { useCredentialSetsForService } from '@/hooks/useCredentialSetsForService'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { useCreateMailbox } from '@/hooks/useMailboxes'
import { startingProcess, type IntakeRow } from '@/utils/intakeRelation'
import { useFeatureStore } from '@/stores/featureStore'
import type {
  Mailbox, MailboxChannel, MailboxCreate, MailboxProvider, SinkType,
} from '@/services/mailboxes'
import { GROUP_ROUTER_SENTENCE, isRowOwnedSet, wahaSetHint } from './groupSourceText'
import { NEW_MAILBOX, errorMessage, recordSinkSentence } from './mailboxesText'
import { useIntakeAgendas, type AdminUserOption } from './useMailboxPageData'

const SELECT_CLASS =
  'w-full text-sm rounded-md border border-border bg-background px-3 py-1.5 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring'

const PREFILL_SOURCES = ['from', 'from_name', 'subject', 'to'] as const

interface PrefillRow {
  slot: string
  source: string
}

interface NewMailboxFormProps {
  providers: readonly MailboxProvider[]
  providersFailed: boolean
  personas: readonly Persona[]
  users: readonly AdminUserOption[]
  /** The workflow library as loaded; the form drops the ones with no published version. */
  workflows: readonly (IntakeRow & { current_version_id: string | null })[] | undefined
  workflowsFailed: boolean
  onCreated: (mb: Mailbox) => void
  onCancel?: () => void
}

export function NewMailboxForm({
  providers, providersFailed, personas, users, workflows, workflowsFailed, onCreated, onCancel,
}: NewMailboxFormProps) {
  const createMutation = useCreateMailbox()

  // Workflow picker options — the library endpoint already excludes
  // archived rows; drop the ones with no published version (the seed
  // path could not start them — SA-101).
  const workflowOptions = (workflows ?? []).filter(
    (w) => w.current_version_id !== null,
  )

  const [providerChoice, setProviderChoice] = useState('')
  const provider = providerChoice || providers[0]?.id || ''
  const providerChannel: MailboxChannel = providers.find((p) => p.id === provider)?.channel ?? 'email'
  // SA-947 (spec P9): a WhatsApp group source names the deployment's WAHA set.
  const creatingGroup = providerChannel === 'whatsapp-group'
  const [credentialSetId, setCredentialSetId] = useState('')
  const wahaSets = useCredentialSetsForService(creatingGroup ? 'waha' : '')
  const wahaSetOptions = (wahaSets.data ?? []).filter((set) => !isRowOwnedSet(set.name))
  const [displayName, setDisplayName] = useState('')
  const [emailAddress, setEmailAddress] = useState('')
  const [personaId, setPersonaId] = useState('')
  const [sinkType, setSinkType] = useState<SinkType>('workflow')
  const [workflowId, setWorkflowId] = useState('')
  const [agendaId, setAgendaId] = useState('')
  const noun = useRecordNoun().singular
  // SA-476 (spec D2): strict — no start is promised before the manifest says process_engine is on.
  const processEngine = useFeatureStore((s) => (s.isLoaded ? s.features.has('process_engine') : null))
  const selectedStart = agendaId ? startingProcess(agendaId, workflows, processEngine) : null
  const selectedProcess = selectedStart?.kind === 'starts' ? selectedStart.process : null
  const [ownerUserId, setOwnerUserId] = useState('')
  const [prefillRows, setPrefillRows] = useState<PrefillRow[]>([
    { slot: 'requester_email', source: 'from' },
  ])
  const [createError, setCreateError] = useState<string | null>(null)

  const updatePrefillRow = (index: number, patch: Partial<PrefillRow>) => {
    setPrefillRows((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }
  const addPrefillRow = () => setPrefillRows((rows) => [...rows, { slot: '', source: 'from' }])
  const removePrefillRow = (index: number) =>
    setPrefillRows((rows) => rows.filter((_, i) => i !== index))
  const agendasQuery = useIntakeAgendas(sinkType === 'record')

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setCreateError(null)
    const prefillMap = prefillRows.reduce<Record<string, string>>((acc, row) => {
      const slot = row.slot.trim()
      if (slot) acc[slot] = row.source
      return acc
    }, {})
    const body: MailboxCreate = creatingGroup
      ? {
          // SA-947 (spec P9): a group source is a router with no email-only field.
          provider,
          display_name: displayName.trim(),
          persona_id: personaId || null,
          sink_type: 'router',
          sink_config: {},
          owner_user_id: ownerUserId || null,
          credential_set_id: credentialSetId || null,
        }
      : {
          provider,
          display_name: displayName.trim(),
          email_address: emailAddress.trim() || null,
          persona_id: personaId || null,
          sink_type: sinkType,
          sink_config:
            sinkType === 'workflow'
              ? { workflow_id: workflowId }
              : sinkType === 'record'
                ? { workflow_id: agendaId }
                : sinkType === 'router'
                  ? {}
                  : null,
          owner_user_id: ownerUserId || null,
          prefill_map: Object.keys(prefillMap).length > 0 ? prefillMap : null,
        }
    try {
      const created = await createMutation.mutateAsync(body)
      setDisplayName('')
      setEmailAddress('')
      setWorkflowId('')
      setAgendaId('')
      setPersonaId('')
      setOwnerUserId('')
      setCredentialSetId('')
      setPrefillRows([{ slot: 'requester_email', source: 'from' }])
      onCreated(created)
    } catch (err) {
      setCreateError(errorMessage(err, 'Failed to create mailbox'))
    }
  }

  const submit = (
    <Button type="submit" disabled={createMutation.isPending} leftIcon={<Plus className="w-4 h-4" />}>
      {createMutation.isPending ? 'Creating…' : 'Create mailbox'}
    </Button>
  )

  return (
    <>
      <h2 className={DETAIL_TITLE_CLASS}>{NEW_MAILBOX}</h2>
      {createError && (
        <Alert variant="destructive">
          <AlertDescription>{createError}</AlertDescription>
        </Alert>
      )}
      <form onSubmit={handleCreate} className="@container space-y-4">
        <div className="grid grid-cols-1 @md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="mailbox-provider" className="mb-1.5 block text-sm font-medium text-foreground">
              Provider
            </label>
            <select
              id="mailbox-provider"
              value={provider}
              onChange={(e) => setProviderChoice(e.target.value)}
              className={SELECT_CLASS}
            >
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            {providersFailed && (
              <p className="mt-1 text-xs text-destructive">
                Failed to load the source types — reload to create a mailbox.
              </p>
            )}
          </div>
          <Input
            label="Display name"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={creatingGroup ? 'Commercial team group' : 'Support Inbox'}
          />
          {!creatingGroup && (
            <Input
              label="Group email address"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="support@example.com"
              hint="The self-mail guard's reference — inbound mail from this address is skipped."
            />
          )}
          {creatingGroup && (
            <div>
              <label htmlFor="mailbox-waha-set" className="mb-1.5 block text-sm font-medium text-foreground">
                WAHA connection
              </label>
              <select
                id="mailbox-waha-set"
                required
                value={credentialSetId}
                onChange={(e) => setCredentialSetId(e.target.value)}
                className={SELECT_CLASS}
              >
                <option value="">Select a credential set…</option>
                {wahaSetOptions.map((set) => (
                  <option key={set.id} value={set.id}>{set.name}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                {wahaSetHint(wahaSets.error, wahaSets.data ? wahaSetOptions.length : null)}
              </p>
            </div>
          )}
          <div>
            <label htmlFor="mailbox-persona" className="mb-1.5 block text-sm font-medium text-foreground">
              Persona (optional)
            </label>
            <select
              id="mailbox-persona"
              value={personaId}
              onChange={(e) => setPersonaId(e.target.value)}
              className={SELECT_CLASS}
            >
              <option value="">None</option>
              {personas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          {creatingGroup ? (
            <div className="@md:col-span-2">
              <span className="mb-1.5 block text-sm font-medium text-foreground">Sink</span>
              <p className="text-sm text-foreground">Router</p>
              <p className="mt-1 text-xs text-muted-foreground">{GROUP_ROUTER_SENTENCE}</p>
            </div>
          ) : (
            <>
            <div>
              <label htmlFor="mailbox-sink-type" className="mb-1.5 block text-sm font-medium text-foreground">
                Sink
              </label>
              <select
                id="mailbox-sink-type"
                value={sinkType}
                onChange={(e) => setSinkType(e.target.value as SinkType)}
                className={SELECT_CLASS}
              >
                <option value="workflow">Workflow</option>
                <option value="record">Quote record</option>
                <option value="router">Router</option>
                <option value="conversation">Staff conversation</option>
              </select>
            </div>
            {sinkType === 'workflow' && (
              <div>
                <label htmlFor="mailbox-workflow" className="mb-1.5 block text-sm font-medium text-foreground">
                  Workflow
                </label>
                <select
                  id="mailbox-workflow"
                  required
                  value={workflowId}
                  onChange={(e) => setWorkflowId(e.target.value)}
                  className={SELECT_CLASS}
                >
                  <option value="">Select a workflow…</option>
                  {workflowOptions.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name} ({w.id})
                    </option>
                  ))}
                </select>
                {workflowsFailed && (
                  <p className="mt-1 text-xs text-destructive">
                    Failed to load workflows — reload to bind a workflow sink.
                  </p>
                )}
              </div>
            )}
            {sinkType === 'record' && (
              <div>
                <label htmlFor="mailbox-agenda" className="mb-1.5 block text-sm font-medium text-foreground">
                  Intake agenda
                </label>
                <select
                  id="mailbox-agenda"
                  required
                  value={agendaId}
                  onChange={(e) => setAgendaId(e.target.value)}
                  className={SELECT_CLASS}
                >
                  <option value="">Select an agenda…</option>
                  {(agendasQuery.data ?? []).map((a) => (
                    <option key={a.workflow_id} value={a.workflow_id}>
                      {a.name} ({a.workflow_id})
                    </option>
                  ))}
                </select>
                {agendasQuery.isError && (
                  <p className="mt-1 text-xs text-destructive">
                    Failed to load intake agendas — reload to bind a quote-record sink.
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  {recordSinkSentence(noun, selectedProcess?.name ?? null)}
                </p>
              </div>
            )}
            {sinkType === 'router' && (
              <p className="text-xs text-muted-foreground @md:col-span-2">
                A router decides what each email is before anything acts on it. Create the mailbox,
                then add its kinds with Edit routing. Until then every message is captured and
                waits for a person to give it a kind.
              </p>
            )}
            </>
          )}
          <div>
            <label htmlFor="mailbox-owner" className="mb-1.5 block text-sm font-medium text-foreground">
              Owner (optional)
            </label>
            <select
              id="mailbox-owner"
              value={ownerUserId}
              onChange={(e) => setOwnerUserId(e.target.value)}
              className={SELECT_CLASS}
            >
              <option value="">None</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name || u.email}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!creatingGroup && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Prefill mapping</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addPrefillRow}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Add row
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Maps an inbound email field to a workflow slot id seeded on intake. For a
              quote-record sink these fill intake fields the extraction leaves empty.
            </p>
            {prefillRows.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  aria-label="Prefill slot"
                  value={row.slot}
                  onChange={(e) => updatePrefillRow(i, { slot: e.target.value })}
                  placeholder="requester_email"
                  className="flex-1"
                />
                <span className="text-muted-foreground text-sm">←</span>
                <select
                  aria-label="Prefill source"
                  value={row.source}
                  onChange={(e) => updatePrefillRow(i, { source: e.target.value })}
                  className={SELECT_CLASS + ' w-auto'}
                >
                  {PREFILL_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Remove prefill row"
                  onClick={() => removePrefillRow(i)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {onCancel ? (
          <div className="flex items-center gap-2">
            {submit}
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
          </div>
        ) : submit}
      </form>
    </>
  )
}
